import type { ChildId, ChildWithProfile, Device, DeviceId } from '@kidshub/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';
import { formatLastSeen } from '../lib/relative-time.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { Badge, Button, ButtonLink, Chip, EmptyState, Icon } from '../ui/kit.js';
import './devices.css';

/**
 * The family's tablets.
 *
 * Switching which child a device is running is offered here because it is a
 * parent action, and `SPEC.md §19.1` requires it to pass the Parent Gate:
 * without that, a child moves to a sibling's profile and inherits their budget,
 * which quietly defeats every screen-time setting. The gate on this side is the
 * parent's own signed-in session.
 *
 * Revoking is deliberately a two-step action rather than a confirm dialog. It
 * invalidates the device's credential immediately and is the right thing to do
 * with a lost tablet, so it must be reachable — and hard to hit by accident.
 */

export const DevicesPage = () => {
  const { t } = useTranslation();
  const devices = useResource('devices', () => apiClient.devices.list());
  const children = useResource('children', () => apiClient.children.list());

  // A reload after a write keeps the previous list on screen rather than
  // blanking it, so the card a parent just acted on does not jump away.
  const list = devices.resource.data?.devices ?? null;

  return (
    <AppShell
      title={t('devices.title')}
      subtitle={t('devices.subtitle')}
      actions={
        <ButtonLink variant="primary" icon="plus" to="/devices/pair">
          {t('devices.pairNew')}
        </ButtonLink>
      }
    >
      {devices.resource.status === 'loading' && list === null ? (
        <p className="t-meta">{t('common.loading')}</p>
      ) : null}

      {devices.resource.status === 'failed' ? (
        <p className="help err" role="alert">
          {t(toErrorKey(devices.resource.error))}
        </p>
      ) : null}

      {list !== null && list.length === 0 ? (
        <EmptyState
          title={t('devices.empty')}
          detail={t('devices.emptyDetail')}
          actions={
            <ButtonLink variant="primary" icon="plus" to="/devices/pair">
              {t('devices.pairNew')}
            </ButtonLink>
          }
        />
      ) : null}

      {list !== null && list.length > 0 ? (
        <div className="stack">
          {list.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              familyChildren={
                children.resource.status === 'ready' ? children.resource.data.children : []
              }
              onChanged={devices.reload}
            />
          ))}
        </div>
      ) : null}

      <p className="t-meta devices-note">{t('devices.offlineNote')}</p>
    </AppShell>
  );
};

interface DeviceCardProps {
  device: Device;
  familyChildren: ChildWithProfile[];
  onChanged: () => void;
}

const DeviceCard = ({ device, familyChildren, onChanged }: DeviceCardProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isConfirmingRevoke, setIsConfirmingRevoke] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const assigned = familyChildren.filter((entry) =>
    device.assignedChildIds.includes(entry.child.id),
  );

  const run = async (action: () => Promise<unknown>) => {
    setErrorKey(null);
    setIsBusy(true);
    try {
      await action();
      onChanged();
    } catch (error) {
      setErrorKey(toErrorKey(error));
    } finally {
      setIsBusy(false);
    }
  };

  const setActiveChild = (childId: ChildId) =>
    run(() => apiClient.devices.setActiveChild({ deviceId: device.id, childId }));

  const revoke = (deviceId: DeviceId) => run(() => apiClient.devices.revoke({ deviceId }));

  const seen =
    device.lastSeenAt === null
      ? t('devices.neverSeen')
      : t('devices.lastSeen', { when: formatLastSeen(device.lastSeenAt, locale) });

  return (
    <article className="card device">
      <span className={device.isOnline ? 'felt-sq sage' : 'felt-sq'}>
        <Icon name="tablet" />
      </span>

      <div className="device-body">
        <h2 className="t-h2">{device.name}</h2>
        <p className="t-meta">
          {t('devices.summary', { platform: t(`devices.platform.${device.platform}`), seen })}
        </p>
        <div className="device-meta">
          <Badge tone={device.isOnline ? 'sage' : 'line'}>
            <span className="dot" />
            {t(device.isOnline ? 'devices.online' : 'devices.offline')}
          </Badge>
          {/* With one profile there is nothing to switch, so the child is a label. */}
          {assigned.length === 1
            ? assigned.map((entry) => (
                <Badge key={entry.child.id} tone="line">
                  {entry.child.name}
                </Badge>
              ))
            : null}
        </div>

        {assigned.length > 1 ? (
          <fieldset className="device-profile">
            <legend className="t-label">{t('devices.activeChild')}</legend>
            <div className="chips">
              {assigned.map((entry) => (
                <Chip
                  key={entry.child.id}
                  selected={entry.child.id === device.activeChildId}
                  disabled={isBusy}
                  onToggle={() => {
                    if (entry.child.id !== device.activeChildId) {
                      void setActiveChild(entry.child.id);
                    }
                  }}
                >
                  {entry.child.name}
                </Chip>
              ))}
            </div>
          </fieldset>
        ) : null}
      </div>

      <div className="device-acts">
        {isConfirmingRevoke ? (
          <>
            <p className="device-confirm">{t('devices.revokePrompt', { name: device.name })}</p>
            <Button
              variant="danger"
              small
              className="btn-danger-solid"
              disabled={isBusy}
              onClick={() => void revoke(device.id)}
            >
              {t('devices.revokeConfirm')}
            </Button>
            <Button variant="ghost" small onClick={() => setIsConfirmingRevoke(false)}>
              {t('common.cancel')}
            </Button>
          </>
        ) : (
          <Button variant="danger" small onClick={() => setIsConfirmingRevoke(true)}>
            {t('devices.revoke')}
          </Button>
        )}
      </div>

      {errorKey === null ? null : (
        <p className="help err device-error" role="alert">
          {t(errorKey)}
        </p>
      )}
    </article>
  );
};
